/**
 * What an editor sees before the app is on screen — connecting, preparing the
 * sign-in, the sign-in itself, a failed start or a crash: the HKCM mark and
 * name on the canvas, with the state below it. The same frame for every one of
 * them, in either theme, so the first seconds already look like HKCM News.
 *
 * `StartError` is the failure card of that frame, shared by the start-up
 * (`Root`) and the error boundary: what went wrong, one way out ("Neu laden"),
 * and the technical detail behind a disclosure — it is for whoever gets the
 * screenshot, not for the editor.
 */

import { CircleAlert, LoaderCircle, RefreshCw } from 'lucide-react'
import type { ReactNode } from 'react'

import HkcmMark from './layout/HkcmMark'
import Button from './ui/Button'
import { CARD, cn, FOCUS_RING } from './ui/styles'

export default function StartScreen({ children }: { children: ReactNode }) {
    return (
        <div className="flex h-screen flex-col items-center justify-center gap-8 overflow-y-auto bg-canvas p-6 text-fg">
            <h1 className="flex items-center gap-3 whitespace-nowrap">
                <HkcmMark className="h-10 w-auto" />
                <span aria-hidden="true" className="text-2xl font-semibold tracking-[0.16em] text-fg-strong">
                    HKCM
                </span>
                <span aria-hidden="true" className="-ml-1.5 text-2xl font-medium text-primary">
                    News
                </span>
                <span className="sr-only">HKCM News</span>
            </h1>
            {children}
        </div>
    )
}

/** A spinner and the step under way ("Verbindung zum Server wird hergestellt …"). */
export function StartProgress({ children }: { children: ReactNode }) {
    return (
        <p role="status" className="flex items-center gap-2 text-sm font-medium text-fg-secondary">
            <LoaderCircle className="h-4 w-4 animate-spin text-primary" aria-hidden="true" />
            {children}
        </p>
    )
}

/** The card in the middle of the start screen (sign-in, failure). */
export function StartCard({ className, children }: { className?: string; children: ReactNode }) {
    return <div className={cn(CARD, 'w-full max-w-md p-6', className)}>{children}</div>
}

export function StartError({ title, children, detail }: { title: string; children: ReactNode; detail?: string | null }) {
    return (
        <StartCard>
            <div role="alert" className="flex items-start gap-3">
                <CircleAlert className="mt-0.5 h-5 w-5 shrink-0 text-danger" aria-hidden="true" />
                <div className="min-w-0 space-y-1">
                    <h2 className="text-base font-semibold text-fg-strong">{title}</h2>
                    <div className="text-sm text-fg-secondary">{children}</div>
                </div>
            </div>
            <Button icon={RefreshCw} onClick={() => window.location.reload()} className="mt-5">
                Neu laden
            </Button>
            {detail && (
                <details className="mt-5 text-xs text-fg-muted">
                    <summary className={cn('cursor-pointer rounded-sm font-semibold', FOCUS_RING)}>Technische Details</summary>
                    <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap wrap-break-word rounded-lg bg-surface-sunken p-3 font-mono text-fg-secondary">
                        {detail}
                    </pre>
                </details>
            )}
        </StartCard>
    )
}
